import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './../../scss/components/pill-tabs.style.scss';

export interface PillTab {
    key: string;
    label: string;
    icon?: React.ReactNode;
}

interface PillTabsProps {
    tabs: PillTab[];
    activeTab: string;
    onTabChange: (key: string) => void;
    ariaLabel?: string;
}

interface IndicatorRect {
    left: number;
    width: number;
}

/**
 * Horizontal pill tabs with a smooth-sliding violet gradient indicator.
 *
 * The active visual is a single absolutely-positioned pill behind the tabs
 * that animates `transform` + `width` between targets — CSS can't tween a
 * gradient background, so this is the only way to get a real slide.
 *
 * On first paint the indicator snaps to position (no slide-in from the left);
 * subsequent tab clicks animate.
 */
export const PillTabs: React.FC<PillTabsProps> = ({ tabs, activeTab, onTabChange, ariaLabel }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
    const [ready, setReady] = useState<boolean>(false);

    const measure = () => {
        const container = containerRef.current;
        if (!container) return;
        const active = container.querySelector<HTMLButtonElement>('[data-active="true"]');
        if (active) {
            setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
        }
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, tabs]);

    useEffect(() => {
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        // Enable transitions only after the first paint so the indicator
        // doesn't slide in from the left on mount.
        const raf = requestAnimationFrame(() => setReady(true));
        return () => {
            window.removeEventListener('resize', onResize);
            cancelAnimationFrame(raf);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={containerRef}
            className={`mi-pill-tabs${ready ? ' is-animated' : ''}`}
            role="tablist"
            aria-label={ariaLabel}
        >
            {indicator && (
                <span
                    className="mi-pill-tabs__indicator"
                    aria-hidden="true"
                    style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
                />
            )}
            {tabs.map((tab) => {
                const isActive = tab.key === activeTab;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        data-active={isActive}
                        className={`mi-pill-tabs__tab${isActive ? ' is-active' : ''}`}
                        onClick={() => onTabChange(tab.key)}
                    >
                        {tab.icon && <span className="mi-pill-tabs__icon" aria-hidden="true">{tab.icon}</span>}
                        <span className="mi-pill-tabs__label">{tab.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default PillTabs;
